import { supabase, isConfigured } from './supabase';
import { touchLocalSyncMeta } from './userDataSync';

// Keys whose changes get synced to Supabase (important data, not UI prefs)
const CLOUD_KEY = /^crm_(leads|lists|tasks|meetings|pipeline_stages|team_views|automations|audit_log|trash|notifications|statusList|objections|bestandskunden|obj_order|obj_panel|call_sessions)(_u[\w-]+)?$/;

// Track keys recently written by THIS browser tab so realtime echoes can be ignored
const _recentWrites = new Map();
function markRecentWrite(key) {
  _recentWrites.set(key, Date.now());
  setTimeout(() => _recentWrites.delete(key), 10000);
}
export function isRecentWrite(key) {
  const t = _recentWrites.get(key);
  return t !== undefined && (Date.now() - t) < 10000;
}

function dispatchCrmError(code, message, detail) {
  window.dispatchEvent(new CustomEvent('crm-error', { detail: { code, message, detail } }));
}

// Keys whose cloud push failed (offline, no session yet) — retried via flushPendingPushes()
const PENDING_PUSH_KEY = 'crm_pending_push';
function getPendingPushKeys() {
  try {
    const raw = localStorage.getItem(PENDING_PUSH_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function markPendingPush(key) {
  try {
    const set = new Set(getPendingPushKeys());
    if (set.has(key)) return;
    set.add(key);
    localStorage.setItem(PENDING_PUSH_KEY, JSON.stringify([...set]));
  } catch {}
}
function clearPendingPush(key) {
  try {
    const set = new Set(getPendingPushKeys());
    if (!set.delete(key)) return;
    localStorage.setItem(PENDING_PUSH_KEY, JSON.stringify([...set]));
  } catch {}
}

async function pushToCloud(key, value) {
  if (!isConfigured) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) { markPendingPush(key); return; }
  const { error } = await supabase.from('user_data').upsert(
    { user_id: user.id, key, value, updated_at: new Date().toISOString() },
    { onConflict: 'user_id,key' }
  );
  if (error) throw error;
  clearPendingPush(key);
}

/** Fehlgeschlagene Cloud-Pushes nachholen (nach Reconnect/Login). */
export async function flushPendingPushes() {
  const keys = getPendingPushKeys();
  for (const key of keys) {
    let value;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) { clearPendingPush(key); continue; }
      value = JSON.parse(raw);
    } catch { clearPendingPush(key); continue; }
    try {
      markRecentWrite(key);
      await pushToCloud(key, value);
    } catch { /* bleibt pending, nächster Versuch beim nächsten Flush */ }
  }
}

const storage = {
  get(key, fallback = null) {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  },

  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      if (CLOUD_KEY.test(key)) {
        touchLocalSyncMeta(key);
        markRecentWrite(key);
        pushToCloud(key, value).catch(err => {
          dispatchCrmError(
            'SYNC_FAIL',
            'Daten konnten nicht mit der Cloud synchronisiert werden.',
            `key=${key} | ${err?.message || err}`
          );
        });
      }
    } catch (err) {
      dispatchCrmError('STORAGE_FAIL', 'Lokaler Speicher konnte nicht geschrieben werden.', String(err));
    }
  },

  setLocal(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      dispatchCrmError('STORAGE_FAIL', 'Lokaler Speicher konnte nicht geschrieben werden.', String(err));
    }
  },

  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
};

export default storage;
